import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, ShieldCheck } from "lucide-react";
import { OFFER } from "../mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const CheckoutForm = ({ amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/payment/success` },
    });
    if (error) {
      setError(error.message || "Payment could not be completed. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <p className="mt-3 font-sans text-sm text-red-600">{error}</p>}
      <button
        type="submit"
        disabled={!stripe || submitting}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand-sage px-8 py-4 font-sans text-sm font-semibold uppercase tracking-[0.14em] text-brand-ivory shadow-[0_10px_30px_-8px_rgba(46,69,57,0.6)] transition-all duration-300 hover:bg-brand-sagedark disabled:opacity-60"
      >
        {submitting ? (<><Loader2 size={16} className="animate-spin" /> Processing…</>) : `Pay Refundable $${amount} Deposit`}
      </button>
      <p className="mt-3 flex items-center justify-center gap-1.5 font-sans text-[11px] text-brand-muted">
        <ShieldCheck size={13} className="text-brand-sage" /> Secured by Stripe · 256-bit encryption
      </p>
    </form>
  );
};

export const InlineDeposit = ({ slug, booking }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(OFFER.deposit);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let apptId = null;
    try { apptId = (JSON.parse(localStorage.getItem("db_active_lead") || "{}") || {}).acuity_appointment_id || null; } catch (_) {}
    fetch(`${BACKEND_URL}/api/payments/create-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        slug,
        origin_url: window.location.origin,
        name: booking?.name || "",
        email: booking?.email || "",
        phone: booking?.phone || "",
        acuity_appointment_id: apptId,
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.client_secret) {
          setClientSecret(d.client_secret);
          setAmount(d.amount);
        } else setFailed(true);
      })
      .catch(() => setFailed(true));
  }, [slug]);

  if (failed) {
    return <p className="font-sans text-sm text-brand-muted">Unable to load payment right now. Please refresh and try again.</p>;
  }
  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center gap-2 py-8 text-brand-muted">
        <Loader2 size={18} className="animate-spin" /> <span className="font-sans text-sm">Loading secure payment…</span>
      </div>
    );
  }

  const options = {
    clientSecret,
    appearance: {
      theme: "flat",
      variables: {
        colorPrimary: "#3E5B4F",
        colorBackground: "#ffffff",
        colorText: "#232320",
        fontFamily: "Inter, system-ui, sans-serif",
        borderRadius: "12px",
      },
    },
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm amount={amount} />
    </Elements>
  );
};
